import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

import ConfirmationModal from "@/components/ui/confirm-dialog";
import {Label} from "@/components/ui/label";
import {Textarea} from "@/components/ui/textarea";

const MAX_REASON_LENGTH = 500;

type PanicFalseAlarmDialogProps = {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
};

export function PanicFalseAlarmDialog({
  open,
  loading = false,
  onClose,
  onConfirm,
}: PanicFalseAlarmDialogProps) {
  const {t} = useTranslation("panic");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError(t("toast.false_alarm_reason_required"));
      return;
    }
    await onConfirm(trimmed.slice(0, MAX_REASON_LENGTH));
  };

  return (
    <ConfirmationModal
      open={open}
      title="Mark as false alarm"
      text="Explain why this alert is being marked as a false alarm."
      confirmButtonLabel="False alarm"
      confirmButtonClassName="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
      loading={loading}
      disableConfirmButton={!reason.trim()}
      onClose={onClose}
      onConfirm={() => void handleConfirm()}
    >
      <div className="flex flex-col gap-2">
        <Label htmlFor="panic-false-alarm-reason">Reason</Label>
        <Textarea
          id="panic-false-alarm-reason"
          value={reason}
          rows={3}
          maxLength={MAX_REASON_LENGTH}
          placeholder="Enter reason"
          onChange={(event) => {
            setError(null);
            setReason(event.target.value);
          }}
        />
        {error ? <p className="text-destructive text-sm">{error}</p> : null}
      </div>
    </ConfirmationModal>
  );
}
